import { Auth } from 'aws-amplify';
import axios from 'axios';
import { useEffect, useState } from 'react';
import process from 'process';

import { Button } from '..';
import { StoreModal } from './StoreModal';
import { DeleteConfirmModal } from './DeleteConfirmModal';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  KeyboardSensor,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const serverUrl = process.env.REACT_APP_SERVER_URL;

export const Store = () => {
  const [trigger, setTrigger] = useState(true);
  const [items, setItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storeModalIsOpen, setStoreModalIsOpen] = useState(false);
  const [editModalIsOpen, setEditModalIsOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [activeId, setActiveId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleOpenStoreModal = () => setStoreModalIsOpen(true);
  const handleCloseStoreModal = () => setStoreModalIsOpen(false);

  const handleOpenEditModal = (item) => {
    setItemToEdit(item);
    setEditModalIsOpen(true);
  };
  const handleCloseEditModal = () => {
    setEditModalIsOpen(false);
    setItemToEdit(null);
  };

  const getToken = async () => {
    const data = await Auth.currentSession();
    return data.idToken.jwtToken;
  };

  useEffect(() => {
    getToken().then(function (token) {
      token = 'Bearer ' + token;
      axios
        .get(serverUrl + 'store/skus', {
          headers: {
            'X-Hemingway-Authorization': token,
          },
        })
        .then((response) => {
          const sortedItems = response.data.sort((a, b) => a.order - b.order);
          setItems(sortedItems);
          setOriginalItems(sortedItems);
          setHasUnsavedChanges(false);
        });
    });
  }, [trigger]);

  const handleCreateItem = async (newItem) => {
    setLoading(true);
    getToken().then(function (token) {
      token = 'Bearer ' + token;

      axios
        .post(
          serverUrl + 'store/sku/create',
          { ...newItem, sku: crypto.randomUUID(), order: items.length },
          {
            headers: {
              'X-Hemingway-Authorization': token,
            },
          }
        )
        .then((resp) => {
          console.log(resp);
          setLoading(false);
          setTrigger(!trigger);
          handleCloseStoreModal();
        })
        .catch((err) => {
          console.log(err);
          console.log(err.response.data);
          setError(err.message + '. Details: ' + err.response.data);
          setLoading(false);
        });
    });
  };

  const handleEditItem = async (updatedItem) => {
    setLoading(true);
    getToken().then(function (token) {
      token = 'Bearer ' + token;

      axios
        .post(
          serverUrl + 'store/sku/update',
          { ...updatedItem, sku: itemToEdit.sku },
          {
            headers: {
              'X-Hemingway-Authorization': token,
            },
          }
        )
        .then((resp) => {
          console.log(resp);
          setLoading(false);
          setTrigger(!trigger);
          handleCloseEditModal();
        })
        .catch((err) => {
          console.log(err);
          console.log(err.response.data);
          setError(err.message + '. Details: ' + err.response.data);
          setLoading(false);
        });
    });
  };

  const handleOpenDeleteModal = (item) => {
    setItemToDelete(item);
    setDeleteModalIsOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalIsOpen(false);
    setItemToDelete(null);
  };

  const handleDeleteItem = async (itemToRemove) => {
    getToken().then(function (token) {
      token = 'Bearer ' + token;

      axios
        .delete(serverUrl + 'store/sku/remove', {
          headers: {
            'X-Hemingway-Authorization': token,
          },
          data: { sku: itemToRemove.sku },
        })
        .then((resp) => {
          setTrigger(!trigger);
        })
        .catch((err) => {
          console.log(err);
        });
    });
  };

  const onDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.sku === active.id);
        const newIndex = items.findIndex((item) => item.sku === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        // Update the order field
        const updatedItems = newItems.map((item, index) => ({
          ...item,
          order: index,
        }));

        setHasUnsavedChanges(true);

        return updatedItems;
      });
    }

    setActiveId(null);
  };

  const saveOrderToServer = (updatedItems) => {
    getToken().then((token) => {
      token = 'Bearer ' + token;

      axios
        .post(
          serverUrl + 'store/skus/reorder',
          {
            skus: updatedItems.map((item) => ({
              sku: item.sku,
              name: item.name,
              price: item.price,
              desc: item.desc,
              category: item.category,
              icon: item.icon,
              order: item.order,
            })),
          },
          {
            headers: {
              'X-Hemingway-Authorization': token,
            },
          }
        )
        .then(() => {
          console.log('SKUs updated successfully');
          setHasUnsavedChanges(false);
          setOriginalItems(updatedItems);
        })
        .catch((err) => {
          console.error('Error updating SKUs:', err);
        });
    });
  };


  const headers = ['Name', 'Description', 'Price', '', ''];

  const SortableItem = ({ item }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.sku });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      cursor: 'grab',
    };

    return (
      <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <td>{item.name}</td>
        <td>{item.desc}</td>
        <td>{item.price}</td>
        <td>
          <div
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(item);
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
        </td>
        <td>
          <div
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteModal(item);
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <>
      <div
        style={{
          marginBottom: '29px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ marginBottom: 0 }}>Store</h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <StoreModal
            isOpen={storeModalIsOpen}
            closeModal={handleCloseStoreModal}
            handleCreateItem={handleCreateItem}
            loading={loading}
            error={error}
            openElement={
              <Button
                style={{ width: '120px', height: '35px', marginTop: 0 }}
                onClick={handleOpenStoreModal}
              >
                New Item
              </Button>
            }
          />
          <StoreModal
            isOpen={editModalIsOpen}
            closeModal={handleCloseEditModal}
            handleEditItem={handleEditItem}
            loading={loading}
            error={error}
            editMode={true}
            defaultValues={itemToEdit}
          />
          <DeleteConfirmModal
            isOpen={deleteModalIsOpen}
            closeModal={handleCloseDeleteModal}
            itemToDelete={itemToDelete}
            onConfirmDelete={handleDeleteItem}
          />
          {hasUnsavedChanges && (
            <Button
              style={{ width: '120px', height: '35px', marginTop: 0 }}
              onClick={() => {
                setItems(originalItems);
                setHasUnsavedChanges(false);
              }}
            >
              Cancel
            </Button>
          )}

          {hasUnsavedChanges && (
            <Button
              style={{ width: '120px', height: '35px', marginTop: 0 }}
              onClick={() => {
                saveOrderToServer(items);
              }}
            >
              Save Order
            </Button>
          )}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(event) => setActiveId(event.active.id)}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.sku)}
          strategy={verticalListSortingStrategy}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {headers.map((header) => (
                  <th
                    key={header}
                    style={{ borderBottom: '1px solid #ddd', padding: '8px' }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <SortableItem key={item.sku} item={item} />
              ))}
            </tbody>
          </table>
        </SortableContext>
      </DndContext>
    </>
  );
};
